import {Button, useMediaQuery} from '@material-ui/core';
import 'Agent/AgentForms/Interactive/components/Interactive.sass';
import 'Agent/AgentForms/Marketing/components/Marketing.sass';
import MarketingViewCartForm from "Agent/AgentForms/Marketing/components/MarketingViewCart";
import {
    changeCartVisibility,
    clearMarketing,
    clearViewCart,
    removeCartItems
} from "Agent/AgentForms/Marketing/ducks/Marketing";
import {
    changePreviousOrdersFilters,
    changeSort,
    clearPreviousOrders,
    clearSort,
    getPreviousOrders, onClickAddToCart
} from "Agent/AgentForms/PreviousOrders/ducks/previousOrders";
import {useBreadcrumbs} from "Core/hooks/useBreadcrumbs";
import * as O from "fp-ts/lib/Option";
import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {RouteComponentProps} from 'react-router-dom';
import {ReducerState} from "rootReducer";
import Column from 'Shared/components/PagedView/Column';
import {withDateFilterProperties, withStringFilterProperties} from "Shared/components/PagedView/Filter";
import PagedView from 'Shared/components/PagedView/PagedView';
import {AgencyFilters} from 'Shared/models/AgencyFilters';
import {addFilterParameters} from "Shared/utilities/addFilterParameters";
import {toFormattedDateTime} from "Shared/utilities/DateFormatter";
import {withValueChangedHook} from "Shared/utilities/withValueChanged";

export type PreviousOrderProps = {
    selectedAgency: AgencyFilters;
} & RouteComponentProps;

const PreviousOrders = (props: PreviousOrderProps) => {
    const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('lg'));

    const dispatch = useDispatch();
    useBreadcrumbs("Previous Orders", props.match.url);
    const {sort, filter, previousOrders} = useSelector((state: ReducerState) => state.previousOrders);
    const {viewCart, inCartVisible, filters: marketingFilters} = useSelector((state: ReducerState) => state.marketing);
    const changeFilter = withValueChangedHook(filter, changePreviousOrdersFilters, dispatch);
    const [cart, setCart] = useState(viewCart);
    const [cartFinal, setCartFinal] = useState(viewCart);
    const [isViewCartBlock, setIsViewCartBlock] = useState(false);
    const [visible, setVisible] = useState(inCartVisible);
    const {selectedAgency} = props;

    const onClickViewCart = () => {
        setIsViewCartBlock(!isViewCartBlock);
    };

    useEffect(() => {
        dispatch(changePreviousOrdersFilters({...filter}));
    }, []);

    useEffect(() => {
        if (!isViewCartBlock) {
            filter.pageNumber = "1";
            dispatch(
                getPreviousOrders(undefined, filter, O.toUndefined(sort), selectedAgency.isNewYork)
            );
        }
    }, [filter, sort, selectedAgency.isNewYork, isViewCartBlock]);

    useEffect(() => {
        filter.pageNumber = "1";
        return () => {

            removeCartItems(inCartVisible, cart, setCart)
            dispatch(changeCartVisibility({...inCartVisible}));
            dispatch(clearPreviousOrders({...filter}));
            dispatch(clearMarketing({...marketingFilters}));
            dispatch(clearViewCart());
            dispatch(clearSort());
        };
    }, [clearPreviousOrders, changePreviousOrdersFilters]);

    const filterProperties = {
        changeFilter,
        filterData: filter,
    };

    return (
        <>
            <div className="backdrop-container" id="backdrop"
                 style={{display: isViewCartBlock ? 'block' : 'none'}}/>
            <PagedView
                pagedViewKey="previousOrder"
                sort={O.toUndefined(sort)}
                onChangeSort={sort => dispatch(changeSort(sort))}
                data={previousOrders}
                loadMore={page => {
                    const nextPage: number = parseInt(filter.pageNumber) + 1;
                    if (page.totalCount > page.result.length) {
                        filter.pageNumber = nextPage.toString();
                        let sortValue = (sort._tag === 'Some') ? '&sortColumn=' + sort.value.column + '&sortOrder=' + sort.value.direction : '';
                        let link;
                        sortValue !== ''
                        ? link = addFilterParameters(filter) + '&isNewYork=' + selectedAgency.isNewYork + sortValue
                        : link = addFilterParameters(filter) + '&isNewYork=' + selectedAgency.isNewYork;

                        page.links = [{
                            "href": "api/documents/v1/marketingForms/orders" + link,
                            "method": "GET",
                            "rel": "nextpage",
                        }];
                    }
                    dispatch(
                        getPreviousOrders(page, filter, O.toUndefined(sort), selectedAgency.isNewYork)
                    );
                }}
                actions={<>
                    <div className={isDesktop ? "action-buttons-desktop" : "action-buttons-mobile"}>

                        <Button
                            color="primary"
                            variant="contained"
                            onClick={onClickViewCart}
                            disabled={(cart.items.size <= 0)}
                        > {(cart.items.size == 0) ? `VIEW CART` : `VIEW CART (${cart.items.size})`}
                        </Button>
                    </div>
                </>
                }
            >
                {columnProps => (
                    <>
                        <Column
                            {...columnProps}
                            title="Date"
                            sortColumnKey="orderDate"
                            filterProperties={withDateFilterProperties(filterProperties, 'orderDate')}
                            default
                        >
                            {previousOrder => toFormattedDateTime(new Date(previousOrder.orderDate!.substring(0, previousOrder.orderDate!.length - 1)))}
                        </Column>
                        <Column
                            {...columnProps}
                            title="Order #"
                            sortColumnKey="orderNumber"
                            filterProperties={withStringFilterProperties(filterProperties, 'orderNumber')}
                            default
                        >
                            {previousOrder => previousOrder.orderNumber}
                        </Column>
                        <Column
                            {...columnProps}
                            title="Form #"
                            sortColumnKey="formNumber"
                            filterProperties={withStringFilterProperties(filterProperties, 'formNumber')}
                            default
                        >
                            {previousOrder => previousOrder.formNumber}
                        </Column>
                        <Column
                            {...columnProps}
                            title="Title"
                            sortColumnKey="product"
                            filterProperties={withStringFilterProperties(filterProperties, 'product')}
                            default
                        >
                            {previousOrder => previousOrder.product}
                        </Column>
                        <Column
                            {...columnProps}
                            title="Quantity"
                            sortColumnKey="quantity"
                            filterProperties={withStringFilterProperties(filterProperties, 'quantity')}
                            default
                        >
                            {previousOrder => previousOrder.quantity}
                        </Column>
                        <Column
                            {...columnProps}
                            title=""
                            isActions
                            default
                        >
                            {data =>
                                <Button
                                    id={`marketing-addtocart-btn-${data.formNumber}`}
                                    className="primary-action-button"
                                    color="primary"
                                    disabled={Number(data.isFormPublished) ? !!(data && data.formNumber && visible.inCartFormNumber.includes(data.formNumber)) : true}
                                    onClick={() => {
                                        onClickAddToCart(data, visible, cart, setCartFinal,setVisible);
                                    }}
                                >
                                    {Number(data.isFormPublished)
                                     ? data && data.formNumber && visible.inCartFormNumber.includes(data.formNumber) ?
                                       <span>ADDED</span> : <span>ADD TO CART</span>
                                     : <span>FORM NO LONGER PUBLISHED</span>}

                                </Button>

                            }
                        </Column>
                    </>
                )}
            </PagedView>
            {isViewCartBlock &&
            <MarketingViewCartForm
                setIsViewCartBlock={setIsViewCartBlock}
                viewCartDetails={viewCart}
            />
            }
        </>
    );
};

export default PreviousOrders;
